import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import { Button, Form, Textfield } from "@natera/form";
import { useErrorController } from "@natera/platform/lib/hooks";
import { ResultCodes } from "@app/service/resultCodes";
import {
  DropdownWrapper,
  FormField,
  PasswordField,
  PasswordNotice,
  Progress,
  usePasswordPolicy,
} from "@app/components";
import { IntlContext, ResetPasswordContext } from "@app/provider";
import { routes } from "@app/routing";
import { useQuery } from "@app/utils";
import { getGraphQLErrorCode } from "@app/provider/error/graphQLErrorCode";

import "./setPassword.scss";

const messages = defineMessages({
  setPasswordTitle: {
    id: "setPasswordTitle",
    defaultMessage: "Reset Password",
  },
  setPasswordCondition: {
    id: "setPasswordCondition",
    defaultMessage: "Your new password cannot be the same as an old password.",
  },
  setPasswordPasswordLabel: {
    id: "setPasswordPasswordLabel",
    defaultMessage: "Create New Password",
  },
  setPasswordRetypePasswordLabel: {
    id: "setPasswordRetypePasswordLabel",
    defaultMessage: "Re-enter New Password",
  },
  setPasswordSaveNewPassword: {
    id: "setPasswordSaveNewPassword",
    defaultMessage: "Save New Password",
  },
  setPasswordDifferentPasswordError: {
    id: "setPasswordDifferentPasswordError",
    defaultMessage: "Passwords must match.",
  },
  setPasswordFieldRequired: {
    id: "setPasswordFieldRequired",
    defaultMessage: "Please fill in this field",
  },
  setPasswordInvalidPassword: {
    id: "setPasswordInvalidPassword",
    defaultMessage: "Invalid password.",
  },
  setPasswordHeader: {
    id: "setPasswordHeader",
    defaultMessage: "Reset Password",
  },
  setPasswordOldMatchesNewPass: {
    id: "setPasswordOldMatchesNewPass",
    defaultMessage: "Your new password cannot be the same as an old password.",
  },
});

enum ValidationErrors {
  REQUIRED = "REQUIRED",
  INVALID_PASSWORD = "INVALID_PASSWORD",
  PASSWORDS_MISMATCH = "PASSWORDS_MISMATCH",
  OLD_MATCHES_NEW = "OLD_MATCHES_NEW",
}

const errorMessagesMapper: Record<ValidationErrors, keyof typeof messages> = {
  [ValidationErrors.REQUIRED]: "setPasswordFieldRequired",
  [ValidationErrors.INVALID_PASSWORD]: "setPasswordInvalidPassword",
  [ValidationErrors.PASSWORDS_MISMATCH]: "setPasswordDifferentPasswordError",
  [ValidationErrors.OLD_MATCHES_NEW]: "setPasswordOldMatchesNewPass",
};

const SetPassword: React.FC = () => {
  const {
    setPassword,
    setPasswordIsLoading,
    setPasswordData,
    checkResetToken,
    resetTokenData,
    setPasswordError,
  } = React.useContext(ResetPasswordContext);
  const { changeLanguage } = useContext(IntlContext);

  const intl = useIntl();
  const query = useQuery();
  const navigate = useNavigate();
  const token = query.get("token");

  const [email, setEmail] = useState("");
  const [password, setPassword$] = React.useState("");
  const [secondaryPassword, setSecondaryPassword$] = React.useState("");
  const [passwordNoticeIsVisible, setPasswordNoticeIsVisible] = useState(false);
  const [passwordErrorKey, setPasswordErrorKey] =
    useState<ValidationErrors | null>(null);
  const [retypePasswordErrorKey, setRetypePasswordErrorKey] =
    useState<ValidationErrors | null>(null);
  const errorController = useErrorController();
  const {
    invalidPasswordRequirement: firstInvalidPasswordRequirement,
    passwordPolicyComplexity: firstPolicyComplexity,
  } = usePasswordPolicy({ password, email });
  const { invalidPasswordRequirement: secondInvalidPasswordRequirement } =
    usePasswordPolicy({ password: secondaryPassword, email });

  const handlePasswordChange: React.ChangeEventHandler<HTMLInputElement> = (
    e
  ) => {
    setPasswordErrorKey(null);
    setPassword$(e.target.value);
  };

  const handlePasswordFieldFocus = () => {
    setPasswordNoticeIsVisible(true);
  };

  const handlePasswordFieldBlur = () => {
    setPasswordNoticeIsVisible(false);
  };

  const handleSecondaryPasswordChange: React.ChangeEventHandler<
    HTMLInputElement
  > = (e) => {
    setRetypePasswordErrorKey(null);
    setSecondaryPassword$(e.target.value);
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const formData = e.currentTarget;

    const passwordValue = formData.password?.value ?? "";
    const secondaryPasswordValue = formData.retypePassword?.value ?? "";

    setPasswordErrorKey(null);
    setRetypePasswordErrorKey(null);

    if (!passwordValue) {
      setPasswordErrorKey(ValidationErrors.REQUIRED);
      return;
    }

    if (!secondaryPasswordValue) {
      setRetypePasswordErrorKey(ValidationErrors.REQUIRED);
      return;
    }

    if (firstInvalidPasswordRequirement || secondInvalidPasswordRequirement) {
      setPasswordErrorKey(ValidationErrors.INVALID_PASSWORD);
      return;
    }

    if (passwordValue !== secondaryPasswordValue) {
      setRetypePasswordErrorKey(ValidationErrors.PASSWORDS_MISMATCH);
      return;
    }

    if (token) {
      setPassword({ token, password: passwordValue });
    }
  };

  React.useEffect(() => {
    setPasswordData &&
      navigate(`${routes.logIn}?code=${ResultCodes.PASSWORD_UPDATED}`, {
        replace: true,
      });
  }, [setPasswordData]);

  useEffect(() => {
    if (token) {
      checkResetToken(token);
    } else {
      navigate(routes.root, {
        replace: true,
      });
    }
  }, []);

  useEffect(() => {
    if (resetTokenData) {
      setEmail(resetTokenData.email);
      if (resetTokenData.language) {
        changeLanguage(resetTokenData.language);
      }
    }
  }, [resetTokenData]);

  useEffect(() => {
    if (passwordErrorKey) {
      errorController.setValidationError(
        "password",
        intl.formatMessage(messages[errorMessagesMapper[passwordErrorKey]])
      );
    } else {
      errorController.clearValidationError("password");
    }

    if (retypePasswordErrorKey) {
      errorController.setValidationError(
        "retypePassword",
        intl.formatMessage(
          messages[errorMessagesMapper[retypePasswordErrorKey]]
        )
      );
    } else {
      errorController.clearValidationError("retypePassword");
    }
  }, [intl.locale, passwordErrorKey, retypePasswordErrorKey]);

  useEffect(() => {
    const errorCode = getGraphQLErrorCode(setPasswordError);

    setPasswordErrorKey(
      errorCode === ResultCodes.OLD_PASSWORD_MATCHES_NEW_PASSWORD
        ? ValidationErrors.OLD_MATCHES_NEW
        : null
    );
  }, [setPasswordError]);

  return (
    <article className="set-password__container">
      <Progress className="set-password-progress" value={100} />
      <h1>{intl.formatMessage(messages.setPasswordHeader)}</h1>
      <p>{intl.formatMessage(messages.setPasswordCondition)}</p>
      <Form
        onSubmit={handleSubmit}
        className="reset-password-form"
        buttons={
          <Button type="submit" loading={setPasswordIsLoading} unelevated>
            {!setPasswordIsLoading &&
              intl.formatMessage(messages.setPasswordSaveNewPassword)}
          </Button>
        }
        noValidate
      >
        <FormField
          required
          label={intl.formatMessage(messages.setPasswordPasswordLabel)}
          error={errorController.getValidationError("password")}
          htmlFor="password"
          withPadding
        >
          <PasswordField
            id="password"
            name="password"
            outline
            placeholder={intl.formatMessage(messages.setPasswordPasswordLabel)}
            onChange={handlePasswordChange}
            onFocus={handlePasswordFieldFocus}
            onBlur={handlePasswordFieldBlur}
          />
        </FormField>

        <DropdownWrapper isOpen={passwordNoticeIsVisible}>
          <PasswordNotice passwordPolicyComplexity={firstPolicyComplexity} />
        </DropdownWrapper>

        <FormField
          required
          label={intl.formatMessage(messages.setPasswordRetypePasswordLabel)}
          error={errorController.getValidationError("retypePassword")}
          htmlFor="retypePassword"
          withPadding
        >
          <Textfield
            type="password"
            id="retypePassword"
            name="retypePassword"
            outline
            placeholder={intl.formatMessage(
              messages.setPasswordRetypePasswordLabel
            )}
            onChange={handleSecondaryPasswordChange}
          />
        </FormField>
      </Form>
    </article>
  );
};

export default SetPassword;
